// WebSocket Service for Real-Time Chat Communication
// Uses native WebSocket API (no STOMP)

import { extractUserInfo, decodeJWT, isTokenExpired } from '../utils/jwtDecoder';
import { isQueueRealtimePayload } from './queueRealtimeSyncService';
import { getEnvVar } from '../utils/runtimeEnv';
import {
  isSessionTakeoverInProgress,
  isPostTakeoverGraceActive,
  beginPostTakeoverGrace,
  clearPostTakeoverGrace,
} from './sessionTakeoverGuard';

/**
 * Get BASE_URL with automatic environment detection (same logic as apiClient.js)
 * Priority:
 * 1. Auto-detect from window.location.hostname (dynamic)
 * 2. Environment variable fallback
 * 3. Default to localhost for development
 */
const getBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol; // 'http:' or 'https:'
  
  // Local development detection
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:8080';
  } else {
    // Production: Use same protocol and hostname as frontend
    return `${protocol}//${hostname}`;
  }
};

const BASE_URL = getBaseUrl();

// Notification service base URL - Use gateway URL
const NOTIFICATION_SERVICE_URL = getEnvVar(
  'REACT_APP_NOTIFICATION_SERVICE_URL',
  `${BASE_URL}/gateway/hd-notification`
);

const WS_LOG = '[WebSocket]';

/** Server/session invalidation — do not reconnect. */
const TERMINAL_CLOSE_CODES = new Set([1000, 4001, 1008]);

/**
 * Handshake auth rejects (blacklist/invalid JWT) usually fail within a few seconds.
 * Three quick closes without ever reaching onopen → terminal auth failure.
 */
const QUICK_HANDSHAKE_FAIL_MS = 4000;
const QUICK_HANDSHAKE_FAIL_THRESHOLD = 3;

function shouldBlockAgentChatAlertForCustomer(message) {
  const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
  if (!token) {
    return false;
  }
  const info = extractUserInfo(token);
  if (!info) {
    return false;
  }
  const rc = String(info.roleCode || '').toUpperCase();
  const roles = Array.isArray(info.roles) ? info.roles : [];
  const isCustomer = rc === 'CUSTOMER' || roles.some((r) => String(r).toUpperCase() === 'CUSTOMER');
  if (!isCustomer) {
    return false;
  }
  const nt = message.notificationType || message.type;
  if (nt === 'chat_request' || nt === 'chat_accepted') {
    return true;
  }
  if (message.type === 'NOTIFICATION' && message.payload?.event === 'NEW_CHAT_REQUEST') {
    return true;
  }
  return false;
}

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.messageHandlers = new Map(); // Map<messageType, Set<callback>>
    this.userId = null;
    /** When true, onclose/onerror must not schedule reconnect (logout / intentional disconnect). */
    this.intentionalShutdown = false;
    /** After terminal auth failure; cleared on disconnect (logout) or new login connect. */
    this.authFailureTerminal = false;
    this.reconnectTimerId = null;
    this.connectStartedAt = 0;
    this.sessionHadOpen = false;
    this.quickCloseWithoutOpenCount = 0;
    this.onConnectCallback = null;
    this.onErrorCallback = null;
    this.takeoverReconnectPending = false;
  }

  /**
   * @returns {boolean} true after logout/disconnect until the next explicit connect()
   */
  isShutdownRequested() {
    return this.intentionalShutdown;
  }

  isAuthFailureTerminal() {
    return this.authFailureTerminal;
  }

  logReconnect(action, detail) {
    console.info(`${WS_LOG} reconnect ${action}:`, detail);
  }

  cancelScheduledReconnect() {
    if (this.reconnectTimerId != null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  resetReconnectCounters() {
    this.reconnectAttempts = 0;
    this.quickCloseWithoutOpenCount = 0;
    this.sessionHadOpen = false;
  }

  /**
   * Terminal auth/session failure — stop reconnect and align with REST 401 cleanup.
   * @param {string} reason
   */
  closeSocketOnly() {
    if (!this.ws) {
      this.isConnected = false;
      return;
    }
    const socket = this.ws;
    this.ws = null;
    this.isConnected = false;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    try {
      if (
        socket.readyState === WebSocket.OPEN
        || socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close(1000, 'Client disconnecting');
      }
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * Sign-in tab: drop stale socket before allow-poll blacklists the old JWT.
   */
  prepareForTakeover() {
    console.info(`${WS_LOG} preparing for session takeover`);
    this.intentionalShutdown = true;
    this.cancelScheduledReconnect();
    this.resetReconnectCounters();
    this.closeSocketOnly();
    this.logReconnect('blocked', { reason: 'session_takeover_prepare' });
  }

  /**
   * After completeSessionChallenge — allow connect with the new JWT.
   */
  completeTakeover() {
    console.info(`${WS_LOG} takeover completed — ready for new session websocket`);
    beginPostTakeoverGrace();
    this.intentionalShutdown = false;
    this.authFailureTerminal = false;
    this.resetReconnectCounters();
    this.takeoverReconnectPending = true;
  }

  /**
   * allow-poll publishes ws-force-disconnect; a delayed 4001 can hit the new socket.
   * Reconnect with the fresh JWT — do not logout.
   */
  handleStaleForceDisconnectDuringGrace() {
    console.info(`${WS_LOG} stale force-disconnect (4001) ignored during post-takeover grace — reconnect scheduled`);
    this.intentionalShutdown = false;
    this.authFailureTerminal = false;
    this.cancelScheduledReconnect();
    this.closeSocketOnly();
    this.logReconnect('scheduled', { reason: 'post_takeover_grace_4001', delayMs: 1500 });

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      const token = this.getAuthTokenFromStorage();
      if (!token || isTokenExpired(token)) {
        return;
      }
      this.connect(token, this.onConnectCallback, this.onErrorCallback);
    }, 1500);
  }

  handleTerminalAuthFailure(reason) {
    if (reason === 'server_close_4001' && isPostTakeoverGraceActive()) {
      this.handleStaleForceDisconnectDuringGrace();
      return;
    }

    if (isSessionTakeoverInProgress()) {
      console.info(`${WS_LOG} auth failure suppressed during takeover`, { reason });
      this.cancelScheduledReconnect();
      this.intentionalShutdown = true;
      this.closeSocketOnly();
      this.logReconnect('blocked', { reason: 'auth_failure_suppressed_takeover', detail: reason });
      return;
    }

    if (this.authFailureTerminal) {
      return;
    }
    this.authFailureTerminal = true;
    this.intentionalShutdown = true;
    this.cancelScheduledReconnect();
    console.warn(`${WS_LOG} auth failure detected:`, reason);
    this.logReconnect('blocked', { reason: 'auth_failure_terminal', detail: reason });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('websocket-auth-failure', { detail: { reason } })
      );
    }

    import('./logoutService')
      .then(({ performUnauthorizedLogout, isLogoutInProgress }) => {
        if (!isLogoutInProgress()) {
          performUnauthorizedLogout(`websocket-${reason}`);
        }
      })
      .catch((err) => {
        console.error(`${WS_LOG} failed to run unauthorized logout:`, err);
      });
  }

  /**
   * Whether an automatic reconnect is allowed after the socket closed.
   */
  shouldAllowReconnect(closeCode, hadOpenThisSession) {
    if (this.intentionalShutdown || this.authFailureTerminal) {
      this.logReconnect('blocked', {
        reason: this.authFailureTerminal ? 'auth_failure_terminal' : 'intentional_shutdown',
        closeCode,
        hadOpenThisSession,
      });
      return false;
    }
    if (closeCode === 4001) {
      if (isPostTakeoverGraceActive()) {
        this.handleStaleForceDisconnectDuringGrace();
        return false;
      }
      this.logReconnect('blocked', { reason: 'terminal_close_code', closeCode, hadOpenThisSession });
      this.handleTerminalAuthFailure('server_close_4001');
      return false;
    }
    if (TERMINAL_CLOSE_CODES.has(closeCode)) {
      this.logReconnect('blocked', { reason: 'terminal_close_code', closeCode, hadOpenThisSession });
      return false;
    }
    const token = this.getAuthTokenFromStorage();
    if (!token) {
      this.logReconnect('blocked', { reason: 'no_token', closeCode, hadOpenThisSession });
      return false;
    }
    if (isTokenExpired(token)) {
      this.logReconnect('blocked', { reason: 'token_expired_locally', closeCode, hadOpenThisSession });
      this.handleTerminalAuthFailure('token_expired');
      return false;
    }
    this.logReconnect('allowed', { closeCode, hadOpenThisSession, attempt: this.reconnectAttempts });
    return true;
  }

  recordCloseWithoutOpen() {
    const elapsed = Date.now() - this.connectStartedAt;
    if (elapsed < QUICK_HANDSHAKE_FAIL_MS) {
      this.quickCloseWithoutOpenCount += 1;
      if (this.quickCloseWithoutOpenCount >= QUICK_HANDSHAKE_FAIL_THRESHOLD) {
        this.handleTerminalAuthFailure('handshake_rejected');
        return true;
      }
    }
    return false;
  }

  getAuthTokenFromStorage() {
    return sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
  }

  /**
   * Extract numeric user ID from JWT for ?userId= on WebSocket URL.
   * Do not use `sub` — it may be an email/UUID; server parses userId as Long only.
   */
  extractUserId(token) {
    if (!token) return null;
    try {
      const d = decodeJWT(token);
      if (!d) return null;
      const raw = d.userid ?? d.USER_ID ?? d.user_id ?? d.userId;
      if (raw === null || raw === undefined || raw === '') return null;
      const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(n) || n <= 0) {
        console.warn('JWT has no numeric userid claim; WebSocket ?userId= cannot be built');
        return null;
      }
      return n;
    } catch (e) {
      console.warn('Could not extract user ID from token:', e);
      return null;
    }
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT token for authentication
   * @param {Function} onConnect - Callback when connected
   * @param {Function} onError - Callback on error
   */
  connect(token, onConnect = null, onError = null) {
    if (isSessionTakeoverInProgress()) {
      console.info(`${WS_LOG} connect deferred — session takeover in progress`);
      if (onError) onError(new Error('WebSocket connect deferred during session takeover'));
      return;
    }

    if (this.authFailureTerminal) {
      console.warn(`${WS_LOG} connect skipped — auth failure terminal`);
      if (onError) onError(new Error('WebSocket session invalid (auth failure)'));
      return;
    }

    this.intentionalShutdown = false;
    this.cancelScheduledReconnect();
    this.onConnectCallback = onConnect;
    this.onErrorCallback = onError;

    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      //console.log('WebSocket already connected');
      if (onConnect) onConnect();
      return;
    }

    try {
      // Get token from storage if not provided
      if (!token) {
        token = this.getAuthTokenFromStorage();
      }

      if (!token) {
        console.error(`${WS_LOG} No token available for connection`);
        if (onError) onError(new Error('No authentication token available'));
        return;
      }

      if (isTokenExpired(token)) {
        console.warn(`${WS_LOG} connect blocked — JWT expired locally`);
        this.handleTerminalAuthFailure('token_expired');
        if (onError) onError(new Error('Authentication token expired'));
        return;
      }

      // Extract user ID from token
      this.userId = this.extractUserId(token);
      if (!this.userId) {
        console.error(`${WS_LOG} Could not extract user ID from token`);
        this.handleTerminalAuthFailure('invalid_token_claims');
        if (onError) onError(new Error('Could not extract user ID from token'));
        return;
      }

      // Convert HTTP/HTTPS URL to WebSocket URL (ws:// or wss://)
      const wsUrl = NOTIFICATION_SERVICE_URL
        .replace(/^http:\/\//, 'ws://')
        .replace(/^https:\/\//, 'wss://');
      
      // Query token for optional handshake auth (hd-notification WebSocketAuthInterceptor); userId for routing.
      const tokenQs = encodeURIComponent(token);
      const wsEndpoint = `${wsUrl}/ws/chat?userId=${this.userId}&token=${tokenQs}`;
      //console.log('Connecting to WebSocket:', wsEndpoint.replace(tokenQs, '(token)'));

      // Create native WebSocket connection
      this.connectStartedAt = Date.now();
      this.sessionHadOpen = false;
      this.ws = new WebSocket(wsEndpoint);

      this.ws.onopen = (event) => {
        //console.log('WebSocket connected:', event);
        this.isConnected = true;
        this.sessionHadOpen = true;
        this.resetReconnectCounters();
        if (this.takeoverReconnectPending) {
          console.info(`${WS_LOG} websocket reconnected with new session after takeover`);
          this.takeoverReconnectPending = false;
        }
        if (this.onConnectCallback) this.onConnectCallback();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          //console.log('WebSocket message received:', message);
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      this.ws.onerror = (error) => {
        console.error(`${WS_LOG} connection error:`, error);
        this.isConnected = false;
        if (this.onErrorCallback) this.onErrorCallback(error);
      };

      this.ws.onclose = (event) => {
        //console.log('WebSocket closed:', event.code, event.reason);
        const hadOpen = this.sessionHadOpen;
        this.isConnected = false;
        this.sessionHadOpen = false;

        if (!hadOpen) {
          if (this.recordCloseWithoutOpen()) {
            return;
          }
        } else {
          this.quickCloseWithoutOpenCount = 0;
        }

        if (!this.shouldAllowReconnect(event.code, hadOpen)) {
          return;
        }
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect();
        } else {
          console.warn(`${WS_LOG} reconnect stopped — max attempts reached`);
          this.logReconnect('blocked', {
            reason: 'max_attempts',
            closeCode: event.code,
            hadOpenThisSession: hadOpen,
          });
        }
      };

    } catch (error) {
      console.error(`${WS_LOG} Error connecting:`, error);
      this.isConnected = false;
      if (this.onErrorCallback) this.onErrorCallback(error);
    }
  }

  /**
   * Attempt to reconnect to WebSocket (uses fresh token from sessionStorage).
   */
  attemptReconnect() {
    if (this.intentionalShutdown || this.authFailureTerminal) {
      this.logReconnect('blocked', { reason: 'shutdown_or_auth_terminal' });
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`${WS_LOG} max reconnection attempts reached`);
      this.logReconnect('blocked', { reason: 'max_attempts_exhausted' });
      return;
    }
    const token = this.getAuthTokenFromStorage();
    if (!token) {
      this.logReconnect('blocked', { reason: 'no_token' });
      return;
    }
    if (isTokenExpired(token)) {
      this.handleTerminalAuthFailure('token_expired');
      return;
    }

    this.reconnectAttempts++;
    this.cancelScheduledReconnect();
    const delayMs = this.reconnectDelay * this.reconnectAttempts;
    this.logReconnect('scheduled', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delayMs,
    });

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      if (this.intentionalShutdown || this.authFailureTerminal) {
        return;
      }
      const freshToken = this.getAuthTokenFromStorage();
      if (!freshToken) {
        return;
      }
      this.connect(freshToken, this.onConnectCallback, this.onErrorCallback);
    }, delayMs);
  }

  /**
   * Handle incoming WebSocket message
   * Routes messages to registered handlers based on message type
   */
  handleMessage(message) {
    if (shouldBlockAgentChatAlertForCustomer(message)) {
      return;
    }

    // Handle both wrapped format ({type: "...", payload: {...}}) and direct format
    let messageType = message.type || message.messageType || 'UNKNOWN';

    if (message.payload) {
      messageType = message.type || 'UNKNOWN';
    } else if (message.messageType) {
      messageType = message.messageType;
    }

    /** @type {Set<Function>} */
    const callbacksOnce = new Set();
    const addHandlers = (typeKey) => {
      if (!typeKey) {
        return;
      }
      const set = this.messageHandlers.get(typeKey);
      if (set) {
        set.forEach((cb) => callbacksOnce.add(cb));
      }
    };

    addHandlers(messageType);

    const chatWireTypes = new Set([
      'MESSAGE',
      'CHAT_MESSAGE',
      'TYPING',
      'JOINED',
      'LEFT',
      'ENDED',
      'CLOSED',
    ]);
    if (message.messageType && chatWireTypes.has(message.messageType)) {
      addHandlers('CHAT_MESSAGE');
      addHandlers('MESSAGE');
    }

    if (message.notificationType && message.notificationType !== messageType) {
      addHandlers(message.notificationType);
    }
    if (message.notificationType) {
      // Route all typed notification messages through generic notification subscribers.
      addHandlers('notification');
      addHandlers('NOTIFICATION');
    }

    if (isQueueRealtimePayload(message)) {
      //console.log(
      //   JSON.stringify({
      //     ns: 'websocket-routing',
      //     stage: 'queue_realtime_frame',
      //     ts: new Date().toISOString(),
      //     eventType: message.eventType,
      //     eventId: message.eventId,
      //     ticketId: message.ticketId,
      //   })
      // );
      addHandlers('QUEUE_REALTIME_SYNC');
      addHandlers(message.eventType);
    }

    callbacksOnce.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  /**
   * After failed takeover — restore normal websocket auth handling.
   */
  abortTakeover() {
    console.info(`${WS_LOG} takeover aborted — websocket auth protection restored`);
    this.intentionalShutdown = false;
    this.authFailureTerminal = false;
    this.resetReconnectCounters();
    this.takeoverReconnectPending = false;
  }

  /**
   * Intentional disconnect (logout). Stops all reconnect timers and suppresses onclose reconnect.
   */
  disconnect() {
    clearPostTakeoverGrace();
    this.intentionalShutdown = true;
    this.authFailureTerminal = false;
    this.cancelScheduledReconnect();
    this.resetReconnectCounters();
    this.logReconnect('blocked', { reason: 'disconnect_intentional' });
    console.info(`${WS_LOG} reconnect stopped intentionally`);

    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.isConnected = false;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        if (
          socket.readyState === WebSocket.OPEN
          || socket.readyState === WebSocket.CONNECTING
        ) {
          socket.close(1000, 'Client disconnecting');
        }
      } catch (e) {
        // ignore close errors during teardown
      }
    } else {
      this.isConnected = false;
    }

    this.messageHandlers.clear();
    this.userId = null;
    //console.log('WebSocket disconnected');
  }

  /**
   * Subscribe to messages of a specific type
   * @param {string} messageType - Message type to subscribe to (e.g., 'CHAT_MESSAGE', 'chat_request')
   * @param {Function} callback - Callback function to handle messages
   */
  subscribe(messageType, callback) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }
    this.messageHandlers.get(messageType).add(callback);
    //console.log('Subscribed to message type:', messageType);
  }

  /**
   * Unsubscribe from messages of a specific type
   * @param {string} messageType - Message type to unsubscribe from
   * @param {Function} callback - Optional callback to remove (if not provided, removes all)
   */
  unsubscribe(messageType, callback = null) {
    if (this.messageHandlers.has(messageType)) {
      if (callback) {
        this.messageHandlers.get(messageType).delete(callback);
      } else {
        this.messageHandlers.delete(messageType);
      }
      //console.log('Unsubscribed from message type:', messageType);
    }
  }

  /**
   * Send a message via WebSocket
   * @param {Object} message - Message object (will be JSON stringified)
   * Must follow wrapper format: {type: "...", payload: {...}}
   */
  sendMessage(message) {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected. Cannot send message.');
      return false;
    }

    try {
      // Validate wrapper format
      if (!message.type) {
        console.error('Message must have a "type" field');
        return false;
      }
      if (!message.payload) {
        console.error('Message must have a "payload" field. Format: {type: "...", payload: {...}}');
        return false;
      }
      
      const jsonMessage = JSON.stringify(message);
      this.ws.send(jsonMessage);
      //console.log('Message sent (wrapper format):', message);
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  /**
   * Send a chat message using wrapper format
   * @param {string} chatSessionId - Chat session ID
   * @param {string} content - Message content
   * @param {string} senderType - 'AGENT' or 'GUEST'
   * @param {string} senderName - Sender name
   * @param {string} senderId - Sender ID (optional)
   */
  sendChatMessage(chatSessionId, content, senderType = 'AGENT', senderName = null, senderId = null) {
    // Use wrapper format: {type: "...", payload: {...}}
    const message = {
      type: 'CHAT_MESSAGE',
      payload: {
        chatSessionId: chatSessionId,
        content: content,
        senderType: senderType,
        senderName: senderName,
        senderId: senderId,
        messageType: 'MESSAGE',
        timestamp: Date.now(),
      }
    };

    return this.sendMessage(message);
  }

  /**
   * Send typing indicator using wrapper format
   * @param {string} chatSessionId - Chat session ID
   * @param {string} senderType - 'AGENT' or 'GUEST'
   * @param {string} senderName - Sender name
   * @param {string} senderId - Sender ID (optional)
   */
  sendTypingIndicator(chatSessionId, senderType = 'AGENT', senderName = null, senderId = null) {
    // Use wrapper format: {type: "...", payload: {...}}
    const message = {
      type: 'TYPING',
      payload: {
        chatSessionId: chatSessionId,
        senderType: senderType,
        senderName: senderName,
        senderId: senderId,
        timestamp: Date.now(),
      }
    };

    return this.sendMessage(message);
  }

  /**
   * Subscribe to user notifications
   * Messages with notificationType will be routed here
   * @param {Function} callback - Callback function to handle notification messages
   */
  subscribeToNotifications(callback) {
    // Subscribe to all notification types
    this.subscribe('chat_request', callback);
    this.subscribe('chat_accepted', callback);
    this.subscribe('notification', callback);
    this.subscribe('NOTIFICATION', callback);
    
    //console.log('Subscribed to notifications');
  }

  /**
   * Unsubscribe from notifications
   */
  unsubscribeFromNotifications() {
    this.unsubscribe('chat_request');
    this.unsubscribe('chat_accepted');
    this.unsubscribe('notification');
    this.unsubscribe('NOTIFICATION');
  }

  /**
   * Remove only the given callback from notification channels (does not affect other subscribers).
   */
  unsubscribeFromNotificationsCallback(callback) {
    if (!callback) {
      return;
    }
    this.unsubscribe('chat_request', callback);
    this.unsubscribe('chat_accepted', callback);
    this.unsubscribe('notification', callback);
    this.unsubscribe('NOTIFICATION', callback);
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean}
   */
  getIsConnected() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
